import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';
import { i18n, Messages } from '@lingui/core';
import { I18nProvider as LinguiI18nProvider } from '@lingui/react';

export const SUPPORTED_LOCALES = [
  'en',
  'de-DE',
  'es-ES',
  'fr-FR',
  'it-IT',
  'ja-JP',
  'ko-KR',
  'nl-NL',
  'pl-PL',
  'pt-PT',
  'ru-RU',
  'tr-TR',
  'zh-CN',
  'zh-TW',
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const LOCALE_STORAGE_KEY = 'ipt:locale';

// Locale display names for UI (in their native language)
export const LOCALE_DISPLAY_NAMES: Record<SupportedLocale, string> = {
  en: 'English',
  'de-DE': 'Deutsch',
  'es-ES': 'Español',
  'fr-FR': 'Français',
  'it-IT': 'Italiano',
  'ja-JP': '日本語',
  'ko-KR': '한국어',
  'nl-NL': 'Nederlands',
  'pl-PL': 'Polski',
  'pt-PT': 'Português',
  'ru-RU': 'Русский',
  'tr-TR': 'Türkçe',
  'zh-CN': '简体中文',
  'zh-TW': '繁體中文',
};

interface I18nContextType {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => Promise<void>;
  isLoading: boolean;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

// Detect user's preferred locale
function detectLocale(): SupportedLocale {
  // Check localStorage first
  const storedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
  if (storedLocale && SUPPORTED_LOCALES.includes(storedLocale as SupportedLocale)) {
    return storedLocale as SupportedLocale;
  }

  // Check browser language
  const browserLang = navigator.language;

  // First try exact match (e.g., 'de-DE', 'zh-CN', 'en')
  if (SUPPORTED_LOCALES.includes(browserLang as SupportedLocale)) {
    return browserLang as SupportedLocale;
  }

  // Special handling for Chinese variants (must come before generic prefix match)
  if (browserLang.startsWith('zh')) {
    // Traditional Chinese regions
    if (['zh-TW', 'zh-HK', 'zh-MO'].includes(browserLang)) {
      return 'zh-TW';
    }
    // Default to Simplified Chinese
    return 'zh-CN';
  }

  // Try matching by language prefix (e.g., browser 'de' or 'de-AT' → our 'de-DE')
  const langCode = browserLang.split('-')[0];
  const match = SUPPORTED_LOCALES.find((loc) => loc.split('-')[0] === langCode);
  if (match) {
    return match;
  }

  // Default to English
  return 'en';
}

// Use import.meta.glob so Vite creates proper chunks for each locale at build time.
const localeCatalogs = import.meta.glob<{ messages: Messages }>(
  '../locales/*/messages.ts',
);

async function loadCatalog(locale: SupportedLocale): Promise<Messages> {
  const path = `../locales/${locale}/messages.ts`;
  const loader = localeCatalogs[path];
  if (!loader) {
    throw new Error(`No catalog found for locale: ${locale}`);
  }
  const module = await loader();
  return module.messages;
}

interface I18nProviderProps {
  children: ReactNode;
}

export function I18nProvider({ children }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<SupportedLocale>(() => detectLocale());
  // `isLoading` reflects an in-flight locale switch and gates the language
  // picker UI. `isInitialized` tracks whether the first catalog has finished
  // loading and gates initial render so users never see raw hash ids before
  // translations resolve. Both start true on cold boot; if i18n was already
  // activated (e.g. by test setup) skip the initial gate entirely.
  const [isLoading, setIsLoading] = useState(() => !i18n.locale);
  const [isInitialized, setIsInitialized] = useState(() => Boolean(i18n.locale));

  // Load catalog and activate locale
  const activateLocale = useCallback(async (newLocale: SupportedLocale) => {
    try {
      const messages = await loadCatalog(newLocale);
      i18n.load(newLocale, messages);
      i18n.activate(newLocale);
    } catch (error) {
      console.error(`Failed to load locale ${newLocale}:`, error);
      // Fall back to English if loading fails
      if (newLocale !== 'en') {
        const enMessages = await loadCatalog('en');
        i18n.load('en', enMessages);
        i18n.activate('en');
      }
    }
  }, []);

  // Initial locale loading
  useEffect(() => {
    const initLocale = async () => {
      setIsLoading(true);
      await activateLocale(locale);
      setIsLoading(false);
      setIsInitialized(true);
    };
    initLocale();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setLocale = useCallback(
    async (newLocale: SupportedLocale) => {
      if (newLocale === locale) return;

      setIsLoading(true);
      await activateLocale(newLocale);
      setLocaleState(newLocale);

      localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);

      setIsLoading(false);
    },
    [locale, activateLocale],
  );

  const value: I18nContextType = {
    locale,
    setLocale,
    isLoading,
  };

  return (
    <I18nContext.Provider value={value}>
      <LinguiI18nProvider i18n={i18n}>
        {isInitialized ? children : null}
      </LinguiI18nProvider>
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextType {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}

// Export i18n instance for use outside of React components (e.g. in test utilities)
export { i18n };
